import { useEffect, useState } from "react";
import { ImagePlus, X as XIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";

// Backend origin (strip the trailing /api) for building media preview URLs.
const MEDIA_ORIGIN = (api.defaults?.baseURL || "").replace(/\/api\/?$/, "");

/**
 * Per-option icon uploader for dropdown / multi_select fields. Lets the user
 * attach an image to each option value (e.g. End Use → curtain / blind /
 * upholstery). Icons print on labels when "Show icons" is on in the Print dialog.
 *
 * Shared by the catalogue Fields page and the org-wide Library › Fields page.
 */
export function OptionIconEditor({ options, icons, onSet }) {
  const [previews, setPreviews] = useState({}); // value -> object/URL
  const [busy, setBusy] = useState({});

  // Resolve previews for options that already have a stored media id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const v of options) {
        const mid = icons[v];
        if (!mid || previews[v]) continue;
        try {
          const r = await api.get(`/media/${mid}/file`);
          const url = r.data?.url || "";
          const src = url.startsWith("http") ? url : MEDIA_ORIGIN + url;
          if (!cancelled) setPreviews((p) => ({ ...p, [v]: src }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.join("|"), JSON.stringify(icons)]);

  const upload = async (value, file) => {
    if (!file) return;
    setPreviews((p) => ({ ...p, [value]: URL.createObjectURL(file) }));
    setBusy((b) => ({ ...b, [value]: true }));
    try {
      const fd = new FormData();
      fd.append("files", file);
      fd.append("role", "field");
      const r = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = Array.isArray(r.data) ? r.data[0] : r.data;
      const mid = created?.id || created?._id;
      if (mid) onSet(value, mid);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setPreviews((p) => { const n = { ...p }; delete n[value]; return n; });
    } finally {
      setBusy((b) => ({ ...b, [value]: false }));
    }
  };

  return (
    <div>
      <Label>
        Option icons{" "}
        <span className="text-xs text-muted-foreground font-normal">
          (optional — printed on labels when “Show icons” is on)
        </span>
      </Label>
      <div className="mt-1.5 space-y-1.5" data-testid="option-icon-editor">
        {options.map((v) => (
          <div key={v} className="flex items-center gap-2 text-sm">
            <div className="w-7 h-7 rounded border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
              {previews[v] ? (
                <img src={previews[v]} alt="" className="w-full h-full object-contain" />
              ) : (
                <ImagePlus className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </div>
            <span className="flex-1 truncate">{v}</span>
            {icons[v] && (
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => { onSet(v, null); setPreviews((p) => { const n = { ...p }; delete n[v]; return n; }); }}
                data-testid={`option-icon-remove-${v}`}
                aria-label="Remove icon"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
            <label className="text-xs text-primary hover:underline cursor-pointer shrink-0">
              {busy[v] ? "Uploading…" : icons[v] ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`option-icon-upload-${v}`}
                onChange={(e) => upload(v, e.target.files?.[0])}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OptionIconEditor;
